/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import cx from 'classnames';
import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useLabels } from '@/hooks/useLabels';
import { Button } from '@/components/Button';
import { SmartLink as Link } from '@/utils/smartLink';
import type { Props } from '@/components/Button';
import styles from './styles.module.scss';
import buttonStyles from '../../Button/styles.module.scss';
import NextImage from '@/components/NextImage';
import { configurableSkuIcon } from '@/constants/images/configurableSkuIcon';
import { routePaths } from '@/constants/routePaths';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';

const greenTickMark2 = {
  src: '/images/green-tickmark2.svg',
  alt: '',
  width: 16,
  height: 11,
};

const labelMap = {
  customize: 'label.configurableSku.customizeYourFloorMat',
  setVehicleToCustomize: 'label_set_vehicle_to_see_pricing',
  addedToCart: 'label_CartButton_Added_To_Cart',
  goToCart: 'label_CategoryPage_body_GoToCart',
  add: 'label_MyAccountVehicle_maintenanceInterval_Add',
  toCart: 'label_ProductDisplayPage_body_ToCart',
  addToCart: 'button_ProductDisplayPage_body_AddToCart',
  updatedCart: 'label_updated_cart',
  updateCart: 'label_update_cart',
  checkNearbyStores: 'label_check_nearby_stores',
};

export const SUCCESS_ANIMATION_DURATION = 2000;

// TODO: rewrite passing "onSuccessfulAdd" through a ref to a prop.
export type CartButtonRef = {
  onSuccessfulAdd: (updateCart?: boolean) => void;
};

const CartButton = forwardRef<CartButtonRef, Props>(function CartButton(props, ref) {
  const [successState, setSuccessState] = useState(false);
  const [successUpdateState, setSuccessUpdateState] = useState(false);
  const [dynamicLabel, setDynamicLabel] = useState('');
  const [dynamicAriaLive, setDynamicAriaLive] = useState<
    'off' | 'assertive' | 'polite' | undefined
  >('polite');

  const { children, ariaLabel, variant, className, ...rest } = props;
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const isMX = locale === countryCodes.mx;
  const addToCart = `${labels.add} ${labels.toCart}`;
  const isCustomizableSku =
    children === labels.customize || children === labels.setVehicleToCustomize;

  const setSuccess = (updateCart?: boolean) => {
    if (updateCart) {
      setSuccessUpdateState(true);
    } else {
      setSuccessState(true);
    }
    setTimeout(() => {
      if (updateCart) {
        setSuccessUpdateState(false);
      } else {
        setSuccessState(false);
      }
    }, SUCCESS_ANIMATION_DURATION);
  };

  useImperativeHandle(
    ref,
    () => ({
      onSuccessfulAdd: (value) => setSuccess(value),
    }),
    [],
  );

  useEffect(() => {
    if (successState) {
      setDynamicLabel(labels.addedToCart);
    }
    if (successUpdateState) {
      setDynamicLabel(labels.updatedCart);
    }

    if (children === labels.goToCart) {
      setDynamicLabel(labels.goToCart);
    } else if (children === labels.checkNearbyStores && ariaLabel !== undefined) {
      setDynamicLabel(ariaLabel);
    } else {
      setDynamicLabel('');
    }

    const shouldSetAriaLabel =
      (children === addToCart ||
        children === labels.addToCart ||
        children === labels.updateCart ||
        children === labels.updatedCart) &&
      ariaLabel !== undefined;

    if (shouldSetAriaLabel) {
      setDynamicAriaLive(undefined);
      setDynamicLabel(ariaLabel);
    }
  }, [
    successState,
    dynamicLabel,
    children,
    labels.goToCart,
    labels.addedToCart,
    ariaLabel,
    labels.addToCart,
    addToCart,
    successUpdateState,
    labels.updatedCart,
    labels.checkNearbyStores,
    labels.updateCart,
  ]);

  const renderContent = () => {
    if (successState || successUpdateState) {
      return (
        <div className={styles.successContent}>
          <NextImage
            src={greenTickMark2.src}
            alt={greenTickMark2.alt}
            width={greenTickMark2.width}
            height={greenTickMark2.height}
          />
          <span className={styles.success}>
            {successState ? labels.addedToCart : labels.updatedCart}
          </span>
        </div>
      );
    }

    if (isCustomizableSku) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NextImage
            src={configurableSkuIcon.src}
            alt={configurableSkuIcon.alt}
            width={configurableSkuIcon.width.desktop}
            height={configurableSkuIcon.height.desktop}
          />
          <span>{children}</span>
        </div>
      );
    }

    return children;
  };

  if (dynamicLabel === labels.goToCart) {
    return (
      <Link
        to={routePaths.cart}
        ariaLabel={dynamicLabel}
        className={cx(
          styles.addToCartButton,
          buttonStyles.outlined,
          buttonStyles.fullWidth,
          buttonStyles.rounded,
          buttonStyles.large,
          styles.nowrapButton,
          className,
        )}
      >
        <span className={buttonStyles.buttonText} data-testid={rest.dataTestId}>
          {renderContent()}
        </span>
      </Link>
    );
  }

  return (
    <Button
      variant={
        children === labels.goToCart || children === labels.checkNearbyStores || isCustomizableSku
          ? 'outlined'
          : variant
      }
      customClass={cx(
        styles.nowrapButton,
        {
          [styles.successButtonMX]:
            isMX && (successState || successUpdateState || children === labels.addedToCart),
        },
        className,
      )}
      {...rest}
      ariaLive={dynamicAriaLive}
      ariaLabel={dynamicLabel}
    >
      {renderContent()}
    </Button>
  );
});

export default CartButton;
